import { cn } from '@/utils/tailwind-util';
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { observer } from 'mobx-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import SvgIcon from '@/assets/svgIcon';
import { useContextModal } from '@/components/Modal';
import useToast from '@/hooks/useToast';
import { useUploadFiles } from '@/services';
import { usePostMessage } from '@/services/chatService';
import { chatKey } from '@/services/queryKey';
import { filesToPayload } from '@/utils/fileUpload';

import { ACCEPT_EXTENSIONS, GroupStatus, TempMessage } from '../constants';
import { FileDto } from '../dto';
import { useChatStore } from '../store';
import { FileThumbnail } from './FileThumbnail';

interface Props {
  getVenueIds: () => string | undefined;
}

export const ChatFooter = observer(({ getVenueIds }: Props) => {
  const store = useChatStore();
  const navigate = useNavigate();
  const { venueId } = useParams();
  const isDisabled = store.currentGroup?.status === GroupStatus.CLOSED;
  const queryClient = useQueryClient();
  const $textarea = useRef<HTMLTextAreaElement>(null);
  const $fileAdd = useRef<HTMLInputElement>(null);
  const $fileThumbnailBox = useRef<HTMLDivElement>(null);
  const [scrollPos, setScrollPos] = useState<'left' | 'right'>('left');
  const [showArrow, setShowArrow] = useState(false);
  const modal = useContextModal();
  const $tempMessageId = useRef<number>(-1);
  const computedVenueId = venueId
    ? Number(venueId)
    : store.currentGroup.venueId;
  const LEFT_BUTTON_STYLE = {
    left: 'invisible',
    right: '',
  };
  const RIGHT_BUTTON_STYLE = {
    left: '',
    right: 'invisible',
  };

  const toast = useToast();
  const handleFileAddClick = () => {
    $fileAdd.current?.click();
  };

  const validateFileSize = (fileList: FileList) => {
    const files = [...fileList];
    const succeeded = files.filter(
      (file) => file.size / Math.pow(1024, 2) < 10
    );
    const failed = files.filter((file) => file.size / Math.pow(1024, 2) > 10);
    return { succeeded, failed };
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    if (store.selectedFiles.length + e.target.files.length > 10) {
      return toast.warning('파일은 최대 10개까지 첨부하실 수 있습니다.', {
        showIcon: true,
      });
    }
    const { succeeded, failed } = validateFileSize(e.target.files);
    if (failed.length > 0) {
      const handleOk = () => {
        store.selectedFiles = [...store.selectedFiles, ...succeeded];
        if ($fileAdd.current) $fileAdd.current.value = '';
        modal.close();
      };
      const handleReselect = () => {
        modal.close();
        if ($fileAdd.current) $fileAdd.current.value = '';
        handleFileAddClick();
      };
      modal.open([
        <div className="w-96 flex flex-col gap-4">
          <p className="font-medium text-xl text-gray-900">
            파일 용량을 확인해주세요
          </p>
          <ul className="pl-5">
            {failed.map((file) => (
              <li className="list-disc">{file.name}</li>
            ))}
          </ul>
          <p>
            선택하신 일부 파일을 첨부할 수 없습니다. 파일 용량을 확인 후
            첨부해주세요.
          </p>
          <div className="flex justify-end gap-4">
            <button
              className="h-12 w-28 rounded bg-[#f6f8ff] text-[#475ac1]"
              onClick={handleReselect}
            >
              다시 선택
            </button>
            <button
              className="rounded w-20 h-12 bg-[#475ac1] text-white"
              onClick={handleOk}
            >
              확인
            </button>
          </div>
        </div>,
        { size: 'auto', isOverlayClickable: false, hasDefaultPadding: true },
      ]);
    } else {
      store.selectedFiles = [...store.selectedFiles, ...e.target.files];
      if ($fileAdd.current) $fileAdd.current.value = '';
    }
  };

  const handleChange = (e: any) => {
    if (e.target.value.length > 500) {
      e.preventDefault();
      toast.warning('글자는 한 번에 최대 500자까지 입력 가능합니다.', {
        showIcon: true,
      });
    } else {
      store.chatMessage = e.target.value;
    }
  };

  const updateLnb = async (res: TempMessage) => {
    if (venueId) {
      await queryClient.invalidateQueries(chatKey.fetchGroups(Number(venueId)));

      await queryClient.refetchQueries(chatKey.fetchGroups(Number(venueId)), {
        active: false,
        inactive: true,
      });
    } else {
      await queryClient.invalidateQueries(
        chatKey.fetchMultiChannelGroups(getVenueIds())
      );
    }
    store.setDefaultSelectedGroup(res.groupId);
    if (store.newChatStarted) {
      store.newChatStarted = false;
      if (window.matchMedia('(max-width: 768px)').matches) {
        navigate(`/chat/venue/${computedVenueId}/group/${res.groupId}`, {
          replace: true,
        });
      }
    }
  };

  const { mutate: mutateMessage, isLoading: isMessageLoading } = usePostMessage(
    computedVenueId,
    async function onSuccess(res: TempMessage) {
      store.removeTempMessage(res.tempMessageId);
      store.setMessageMapFromData([res]);
      store.initMessageStatus();
      updateLnb(res);
    },
    function onError() {
      if (store.getMessage($tempMessageId.current)) {
        store.getMessage($tempMessageId.current)!.failed = true;
      }
      toast.warning('메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.', {
        showIcon: true,
      });
    }
  );

  const { mutate: mutateFile, isLoading: isFileLoading } = useUploadFiles(
    function onSuccess(res: FileDto[]) {
      const fileIds = res.map((fileDto) => fileDto.id);
      const message = store.getMessage($tempMessageId.current);
      message!.fileIds = fileIds;
      mutateMessage({
        tempMessageId: $tempMessageId.current,
        groupId: message!.groupId,
        content: message!.content.trim() === '' ? '' : message!.content,
        fileIds,
      });
    },
    function onError() {
      store.getMessage($tempMessageId.current)!.failed = true;
      toast.warning('메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.', {
        showIcon: true,
      });
    }
  );

  const handleSubmit = async () => {
    if (isFileLoading || isMessageLoading) {
      return;
    }
    if (store.chatMessage.trim() === '' && store.selectedFiles.length === 0) {
      store.chatMessage = '';
      return;
    }
    if (store.selectedFiles.length > 0) {
      $tempMessageId.current = store.appendTempMessage();
      mutateFile(filesToPayload(store.selectedFiles));
    } else {
      $tempMessageId.current = store.appendTempMessage();
      const message = store.getMessage($tempMessageId.current);
      mutateMessage({
        tempMessageId: $tempMessageId.current,
        groupId: message!.groupId,
        content: message!.content.trim() === '' ? '' : message!.content,
        fileIds: [],
      });
    }
    store.initMessageStatus();
  };

  const handleKeyDown = (e: any) => {
    if (
      e.code === 'Enter' &&
      !e.shiftKey &&
      !isMessageLoading &&
      !isFileLoading
    ) {
      // 한글 키보드 입력 시 이벤트 두 번 호출되는 이슈
      // 조합형 문자인 한글이 조합 중인지, 완성된 문자인지 판단하기 위한 nativeEvent.isComposing
      if (
        (store.chatMessage.trim() !== '' || store.selectedFiles.length !== 0) &&
        !e.nativeEvent.isComposing
      ) {
        handleSubmit();
      }
      e.preventDefault();
    }
  };

  useEffect(() => {
    store.selectedFiles = [];
    store.chatMessage = '';
  }, [store.currentGroup?.id]);

  useEffect(() => {
    if ($textarea.current) {
      $textarea.current.style.height = 'auto';
      $textarea.current.style.height = $textarea.current.scrollHeight + 'px';
    }
  }, [store.chatMessage.length]);

  useEffect(() => {
    if (
      $fileThumbnailBox.current &&
      $fileThumbnailBox.current.scrollWidth >
        $fileThumbnailBox.current.clientWidth
    ) {
      setShowArrow(true);
    } else {
      setShowArrow(false);
    }
  }, [store.selectedFiles.length]);

  return (
    <div
      className={cn(
        'relative mx-8 flex h-auto flex-col rounded-lg border-[1px] border-solid border-[#ececec] p-5 gap-5',
        (isDisabled || (!venueId && store.groupList.length === 0)) &&
          'bg-gray-100'
      )}
    >
      <textarea
        value={store.chatMessage}
        rows={1}
        ref={$textarea}
        style={{ resize: 'none' }}
        disabled={isDisabled || (!venueId && store.groupList.length === 0)}
        className="flex max-h-96 min-h-28 overflow-y-auto text-base font-medium text-[#171717] focus:outline-none disabled:bg-inherit"
        placeholder={
          !venueId && store.groupList.length === 0
            ? '고객사 선택 시에는 새 메시지를 전송할 수 없습니다'
            : isDisabled
            ? '종료된 문의에서는 메시지를 전송할 수 없습니다'
            : '메시지를 입력해주세요'
        }
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {store.selectedFiles.length > 0 && (
        <div
          ref={$fileThumbnailBox}
          className="flex h-[68px] items-end gap-3 overflow-x-auto"
        >
          {store.selectedFiles.map((file) => (
            <FileThumbnail key={file.name} file={file} />
          ))}
        </div>
      )}
      <div className="flex h-10 justify-between">
        <div className="flex gap-3">
          <button
            onClick={handleFileAddClick}
            disabled={isDisabled || (!venueId && store.groupList.length === 0)}
          >
            <SvgIcon name="upload" />
            <input
              onChange={handleFileSelect}
              ref={$fileAdd}
              type="file"
              multiple
              accept={ACCEPT_EXTENSIONS}
              className="hidden"
            />
          </button>
        </div>
        <button
          onClick={handleSubmit}
          disabled={
            isDisabled ||
            (!venueId && store.groupList.length === 0) ||
            isMessageLoading ||
            isFileLoading
          }
          className="w-16 rounded bg-[#4f64d7] font-semibold text-white disabled:bg-[#c1c1c1] disabled:text-[#f5f5f5]"
        >
          전송
        </button>
      </div>
      {showArrow && (
        <div className="absolute bottom-24 left-0 flex w-full items-center justify-between">
          <button
            className={cn(LEFT_BUTTON_STYLE[scrollPos])}
            onClick={() => {
              setScrollPos('left');
              $fileThumbnailBox.current?.scrollBy({
                left: -$fileThumbnailBox.current.scrollWidth,
                behavior: 'smooth',
              });
            }}
          >
            <SvgIcon styles={'rotate-180'} name="chatArrowRightIcon" />
          </button>
          <button
            className={cn(RIGHT_BUTTON_STYLE[scrollPos])}
            onClick={() => {
              setScrollPos('right');
              $fileThumbnailBox.current?.scrollBy({
                left: $fileThumbnailBox.current.scrollWidth,
                behavior: 'smooth',
              });
            }}
          >
            <SvgIcon name="chatArrowRightIcon" />
          </button>
        </div>
      )}
    </div>
  );
});
